import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import { COUNTRIES } from "../utils/countries";

import { apiGet, apiPost, apiPatch } from "../api/request";
const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "fr", label: "Français" },
  { value: "zh-CN", label: "中文 (简体)" },
  { value: "zh-TW", label: "中文 (繁體)" },
  { value: "it", label: "Italiano" },
  { value: "es", label: "Español" },
  { value: "de", label: "Deutsch" },
  { value: "ja", label: "日本語" },
];

const EMPTY = {
  company_name: "",
  company_address: "",
  country: "",
  vat_number: "",
  default_language: "en",
  email_general: "",
  email_finance: "",
  email_service: "",
  email_sales: "",
  smtp_host: "",
  smtp_port: "587",
  smtp_user: "",
  smtp_password: "",
  smtp_from_email: "",
  smtp_from_name: "",
  smtp_use_tls: true,
  bank_name: "",
  account_name: "",
  bank_code: "",
  account_number: "",
  swift_code: "",
  bank_address: "",
};

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">
        {label}{required && <span className="text-red-600 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

export default function FirstLoginSetupModal() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const role = (user?.role || "").toLowerCase();
  const isAdmin = role === "admin" || role === "superuser";

  const { data: settings, isLoading } = useQuery({
    queryKey: ["company-settings"],
    queryFn: () => apiGet("/api/company-settings/").then((r) => r.data),
    enabled: isAdmin,
    staleTime: 0,
  });

  const { data: bankAccounts = [], isLoading: loadingBanks } = useQuery({
    queryKey: ["company-bank-accounts"],
    queryFn: () => apiGet("/api/company-settings/bank-accounts").then((r) => r.data),
    enabled: isAdmin,
    staleTime: 0,
  });

  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (settings) {
      setForm((f) => ({
        ...f,
        company_name: settings.company_name ?? "",
        company_address: settings.company_address ?? "",
        country: settings.country ?? "",
        vat_number: settings.vat_number ?? "",
        default_language: settings.default_language ?? "en",
        email_general: settings.email_general ?? "",
        email_finance: settings.email_finance ?? "",
        email_service: settings.email_service ?? "",
        email_sales: settings.email_sales ?? "",
        smtp_host: settings.smtp_host ?? "",
        smtp_port: String(settings.smtp_port ?? "587"),
        smtp_user: settings.smtp_user ?? "",
        smtp_password: "",
        smtp_from_email: settings.smtp_from_email ?? "",
        smtp_from_name: settings.smtp_from_name ?? "",
        smtp_use_tls: settings.smtp_use_tls ?? true,
      }));
    }
  }, [settings]);

  const isComplete = !!(
    settings?.company_name?.trim() &&
    settings?.country?.trim() &&
    settings?.smtp_host?.trim() &&
    settings?.smtp_from_email?.trim() &&
    bankAccounts?.length
  );
  const missing = [];
  if (!settings?.company_name?.trim()) missing.push("Company Name");
  if (!settings?.country?.trim()) missing.push("Country");
  if (!settings?.smtp_host?.trim()) missing.push("SMTP Host");
  if (!settings?.smtp_from_email?.trim()) missing.push("From Email");
  if (!bankAccounts?.length) missing.push("Bank Account");

  const showModal = isAdmin && !isLoading && !loadingBanks && settings && !dismissed && !isComplete;
  if (!showModal) return null;

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError("");
    if (!(form.company_name || "").trim()) {
      setError("Company Name is required.");
      setSaving(false);
      return;
    }
    if (!(form.country || "").trim()) {
      setError("Country is required.");
      setSaving(false);
      return;
    }
    try {
      const companyPayload = {
        company_name: form.company_name || null,
        company_address: form.company_address || null,
        country: form.country || null,
        vat_number: form.vat_number || null,
        default_language: form.default_language || "en",
        email_general: form.email_general || null,
        email_finance: form.email_finance || null,
        email_service: form.email_service || null,
        email_sales: form.email_sales || null,
        smtp_host: form.smtp_host || null,
        smtp_port: form.smtp_port ? parseInt(form.smtp_port, 10) : null,
        smtp_user: form.smtp_user || null,
        smtp_from_email: form.smtp_from_email || null,
        smtp_from_name: form.smtp_from_name || null,
        smtp_use_tls: !!form.smtp_use_tls,
        setup_completed: true,
      };
      if ((form.smtp_password || "").trim()) {
        companyPayload.smtp_password = form.smtp_password.trim();
      }
      // Auto-derive the public base URL from the URL used to connect.
      if (!settings.app_base_url) {
        companyPayload.app_base_url = window.location.origin;
      }

      await apiPatch("/api/company-settings/", companyPayload);

      if ((form.bank_name || "").trim() || (form.account_number || "").trim()) {
        await apiPost("/api/company-settings/bank-accounts", {
          bank_name: form.bank_name || null,
          account_name: form.account_name || null,
          bank_code: form.bank_code || null,
          account_number: form.account_number || null,
          swift_code: form.swift_code || null,
          bank_address: form.bank_address || null,
          is_default: true,
        });
      }

      await queryClient.invalidateQueries({ queryKey: ["company-settings"] });
      await queryClient.invalidateQueries({ queryKey: ["company-bank-accounts"] });
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  function handleSkip() {
    setDismissed(true);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-800">Initial Setup</h2>
          <p className="text-sm text-gray-500 mt-1">
            Fill in the essential information before adding consultants, clients or vendors.
          </p>
          {missing.length > 0 && (
            <p className="text-xs text-amber-600 mt-2">
              Still missing: {missing.join(", ")}. Complete them or click "Skip for now".
            </p>
          )}
        </div>

        <form onSubmit={handleSave} className="overflow-y-auto px-6 py-4 space-y-5">
          {/* Company */}
          <section>
            <h3 className="text-sm font-bold text-blue-600 uppercase tracking-wide mb-3">1. Company</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Company Name" required>
                <input className="form-input w-full" required value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="e.g. ALG Intelligence" />
              </Field>
              <Field label="Country" required>
                <select className="form-select w-full" required value={form.country || ""} onChange={(e) => set("country", e.target.value)}>
                  <option value="">—</option>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Company Address">
                <textarea className="form-input w-full" rows={4} value={form.company_address} onChange={(e) => set("company_address", e.target.value)} placeholder="Full address..." />
              </Field>
              <Field label="VAT Number">
                <input className="form-input w-full" value={form.vat_number} onChange={(e) => set("vat_number", e.target.value)} placeholder="e.g. FR123456789" />
              </Field>
              <Field label="Default Language">
                <select className="form-select w-full" value={form.default_language || "en"} onChange={(e) => set("default_language", e.target.value)}>
                  {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
              </Field>
              <Field label="General Email">
                <input className="form-input w-full" type="email" value={form.email_general} onChange={(e) => set("email_general", e.target.value)} placeholder="info@company.com" />
              </Field>
            </div>
          </section>

          {/* SMTP */}
          <section>
            <h3 className="text-sm font-bold text-blue-600 uppercase tracking-wide mb-3">2. Email (SMTP)</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="SMTP Host">
                <input className="form-input w-full" value={form.smtp_host} onChange={(e) => set("smtp_host", e.target.value)} placeholder="smtp.example.com" />
              </Field>
              <Field label="SMTP Port">
                <input className="form-input w-full" type="number" value={form.smtp_port} onChange={(e) => set("smtp_port", e.target.value)} placeholder="587" />
              </Field>
              <Field label="SMTP User">
                <input className="form-input w-full" value={form.smtp_user} onChange={(e) => set("smtp_user", e.target.value)} placeholder="user@example.com" />
              </Field>
              <Field label={settings?.smtp_password_set ? "SMTP Password (saved)" : "SMTP Password"}>
                <input className="form-input w-full" type="password" name="smtp_password" autoComplete="new-password" value={form.smtp_password} onChange={(e) => set("smtp_password", e.target.value)} placeholder={settings?.smtp_password_set ? "••••••••" : ""} />
              </Field>
              <Field label="From Email">
                <input className="form-input w-full" type="email" value={form.smtp_from_email} onChange={(e) => set("smtp_from_email", e.target.value)} placeholder="invoices@example.com" />
              </Field>
              <Field label="From Name">
                <input className="form-input w-full" value={form.smtp_from_name} onChange={(e) => set("smtp_from_name", e.target.value)} placeholder="ALG Intelligence" />
              </Field>
            </div>
            <label className="flex items-center gap-2 mt-3 text-sm text-gray-700">
              <input type="checkbox" className="w-4 h-4" checked={!!form.smtp_use_tls} onChange={(e) => set("smtp_use_tls", e.target.checked)} />
              Use STARTTLS
            </label>
          </section>

          {/* Bank account */}
          <section>
            <h3 className="text-sm font-bold text-blue-600 uppercase tracking-wide mb-3">3. Bank Account</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Bank Name">
                <input className="form-input w-full" value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} placeholder="e.g. HSBC" />
              </Field>
              <Field label="Account Name">
                <input className="form-input w-full" value={form.account_name} onChange={(e) => set("account_name", e.target.value)} />
              </Field>
              <Field label="Bank Code">
                <input className="form-input w-full" value={form.bank_code} onChange={(e) => set("bank_code", e.target.value)} />
              </Field>
              <Field label="Account Number">
                <input className="form-input w-full" value={form.account_number} onChange={(e) => set("account_number", e.target.value)} />
              </Field>
              <Field label="SWIFT">
                <input className="form-input w-full" value={form.swift_code} onChange={(e) => set("swift_code", e.target.value)} />
              </Field>
            </div>
          </section>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex items-center justify-end gap-3 pt-2 pb-1">
            <button type="button" className="btn btn-secondary" onClick={handleSkip} disabled={saving}>
              Skip for now
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving..." : "Save & finish"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
